import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { RoleToLinkMapper } from "../../../experience/utils/UrlUtils";
import "./patientlist.css";
import { useGetRole } from "../../generic/hooks/useGetRole";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { currentPatientData, patientName } from "../../../components/recoil/patientListRecoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import { Space, Tooltip } from "antd";
import { getFullName } from "../../careprovider/visits/utils";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";

const TableColumns = () => {
    const navigate = useNavigate();
    const role = useGetRole();
    const setPatientName = useSetRecoilState(patientName);
    const setCurrentPatientData = useSetRecoilState(currentPatientData);
    const organizationIdentifier = useGetOrgIdentifier()
    const [alphabetizeByFamilyName, setAlphabetizeByFamilyName] = useState(false)

    // Feature Flags
    const [isVisitRequestOptionAllowed, setVisitRequestOptionAllowed] = useState(false);
    const [isViewPatientProfileOptionAllowed, setViewPatientProfileOptionAllowed] = useState(false);
    const [isViewAuditLogAllowed, setViewAuditLogAllowed] = useState(false);


    const configuration = useRecoilValue(currentConfiguration);

    useEffect(() => {

        // Visit Request Option
        const configurationVisitRequestOption = configuration[ConfigurationConstants.FEATUREACCESS_PATIENTLIST_VISITREQUEST_OPTION];
        setVisitRequestOptionAllowed(configurationVisitRequestOption?.visibility);

        // View Profile Option
        const configurationViewPatientProfileOption = configuration[ConfigurationConstants.FEATUREACCESS_PATIENTLIST_VIEW_PATIENT_PROFILE_OPTION];
        setViewPatientProfileOptionAllowed(configurationViewPatientProfileOption?.visibility);

        // View Audit logs
        const configurationViewAuditLogs = configuration[ConfigurationConstants.FEATUREACCESS_PATIENTLIST_VIEW_AUDIT_LOGS];
        setViewAuditLogAllowed(configurationViewAuditLogs?.visibility);
    }, [configuration]);

    useEffect(() => {
        const getAlphabetizeConfig = async () => {
            const payload = {
                intent: "get_configuration",
                payload: { configurationKey: ["niramaya.alphabetize.patientlist"] },
                context: { organizationIdentifier }
            };

            const result = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
            const response = await result.json();
            const alphabetizeConfig = JSON.parse(response[0]?.configurationValue || "{}");

            setAlphabetizeByFamilyName(alphabetizeConfig?.alphabetizeByFamilyName);
        };

        getAlphabetizeConfig();
    }, [organizationIdentifier]);
      

    const handleCreateVolunteerRequest = (rowClicked) => {
        setCurrentPatientData(rowClicked);
        setPatientName(rowClicked.givenName + " " + rowClicked.familyName);
        navigate(RoleToLinkMapper(role) + "request-for-volunteering-service?identifier=" + rowClicked["userIdentifier"]);
    };


    const handleProfileView = (rowClicked) => {
        navigate(RoleToLinkMapper(role) + "patient-profile?identifier=" + rowClicked["userIdentifier"]);
    };

    const handleAuditLogsView = (rowClicked) => {
        navigate(RoleToLinkMapper(role) + "audit-logs?patient=" + rowClicked["userIdentifier"]);
    };

    const columns = [
        {
            title: "MRN",
            dataIndex: "medicalRecordNumber",
            sorter: {
                compare: (a, b) => a.medicalRecordNumber - b.medicalRecordNumber,
            },
            render: (text) => <div className="number-cell">{text}</div>,
        },
        {
            title: "Patient Name",
            dataIndex: "givenName",
            sorter: {
                compare: (a, b) => alphabetizeByFamilyName ? a.familyName.localeCompare(b.familyName) : a.givenName.localeCompare(b.givenName),
            },
            render: (text, record) => (
                <span>
                    {getFullName(record?.givenName, record?.familyName)}
                </span>
            )
        },
    ];

    columns.push({
        title: "Action",
        dataIndex: "action",
        render: (text, record) => (
            <Space size={20} className="patientlist-action-items">
                {isVisitRequestOptionAllowed && (
                    <Tooltip title="Request Volunteer" placement="bottom" >
                        <>
                            <SmartIcon iconKey={"volunteerRequest"} handleIconClick={() => handleCreateVolunteerRequest(record)} />
                        </>
                    </Tooltip>
                )}
                {isViewPatientProfileOptionAllowed && (
                    <Tooltip title="View profile" placement="bottom" >
                        <>
                            <SmartIcon iconKey={"profile"} handleIconClick={() => handleProfileView(record)} />
                        </>
                    </Tooltip>
                )}
                {isViewAuditLogAllowed && (
                    <Tooltip title="View audit logs" placement="bottom" >
                        <>
                            <SmartIcon iconKey={"audit-logs"} handleIconClick={() => handleAuditLogsView(record)} />
                        </>
                    </Tooltip>
                )}
            </Space>
        ),
    });

    return { columns };
};

export default TableColumns;
